import { cannonAt, coordEq } from "@dailywars/core/engine";

import type { SelectionState } from "#/game/selection/types";
import { commitPrepareUnload } from "#/game/store/gameMutations";
import type { StoreState } from "#/game/store/types";

import type { TileSelectContext } from "./tileSelectContext";
import { selectCannonInspect, selectEnemyInspect } from "./tileSelectNoneInspect";
import {
  confirmAttackFromOptions,
  confirmCannonAttackFromOptions,
  canShowUnloadCursor,
  commitSelectedUnitAction,
  startMovePreview,
  startMovePreviewToAlly,
} from "./tileSelectSharedHelpers";

type UnitSelection = Extract<SelectionState, { mode: "unit" }>;

function handleUnitEnemyAndCannonBranch(
  ctx: TileSelectContext,
  sel: UnitSelection,
): StoreState | undefined {
  const { state, local, tile, clickedUnit } = ctx;

  if (clickedUnit && clickedUnit.owner !== local.currentPlayer) {
    const option = sel.attackOptions.find((o) => o.targetId === clickedUnit.id);
    if (!option) return selectEnemyInspect(ctx);
    return confirmAttackFromOptions({
      state,
      unitId: sel.unitId,
      path: [],
      clickedUnit,
      forecast: option.forecast,
    });
  }

  const cannonOption = sel.attackOptions.find((o) => o.targetPos && coordEq(o.targetPos, tile));
  if (cannonOption?.targetPos) {
    return confirmCannonAttackFromOptions({
      state,
      unitId: sel.unitId,
      path: [],
      targetPos: cannonOption.targetPos,
      forecast: cannonOption.forecast,
    });
  }
  if (cannonAt(local, tile)) {
    return selectCannonInspect(ctx);
  }
  return undefined;
}

export function handleUnitMode(ctx: TileSelectContext, sel: UnitSelection): StoreState {
  const { state, local, tile, clickedUnit, clickedOwnSelectable } = ctx;

  if (clickedOwnSelectable?.id === sel.unitId) {
    return commitSelectedUnitAction({ state, local, sel });
  }

  const enemyOrCannon = handleUnitEnemyAndCannonBranch(ctx, sel);
  if (enemyOrCannon !== undefined) return enemyOrCannon;

  if (sel.reachable.some((c) => coordEq(c, tile))) {
    const unit = local.units.find((u) => u.id === sel.unitId);
    if (unit && canShowUnloadCursor(unit) && coordEq(tile, unit.pos)) {
      return commitPrepareUnload(state, sel.unitId);
    }
    return startMovePreview({ state, local, sel, tile });
  }

  if (clickedUnit?.owner === local.currentPlayer) {
    const isLoadOrJoin =
      sel.loadOptions.some((o) => o.targetId === clickedUnit.id) ||
      sel.joinOptions.some((o) => o.targetId === clickedUnit.id);
    if (isLoadOrJoin) {
      return startMovePreviewToAlly({ state, local, sel, ally: clickedUnit });
    }
  }

  return { ...state, selection: { mode: "none" }, previewAnimation: null };
}
